<template>
  <div v-if="fileId" class="image-upload">
    <ImageUploadHeader
      v-if="!readOnly"
      :class="[
        'image-upload-header',
        { 'image-upload-header--inline': headerMenuInline },
      ]"
      :show-edit-button="hasPreviewImage"
      :header-menu-inline="headerMenuInline"
      :can-reposition-image="canRepositionImage"
      :can-restore-original-image="canRestoreOriginalImage"
      :can-use-unsplash="allowUnsplash"
      :can-delete-image="canDeleteImage"
      @onRestoreOriginalImage="resetForm"
      @onRepositionImage="openRepositionTool"
      @onOpenFileExplorer="openFileExplorer"
      @onOpenUnsplash="openUnsplash"
      @onDeleteImage="onDeleteImage"
    >
      <template #header-title>
        <slot name="header-title" />
      </template>
      <template #header-tooltip-description-text>
        <slot name="header-tooltip-description-text" />
      </template>
      <template #header-tooltip-recommended-size>
        <slot name="header-tooltip-recommended-size" />
      </template>
    </ImageUploadHeader>

    <section
      role="button"
      aria-label="Drag and drop area"
      tabindex="0"
      @drop="onDropImage"
      @dragover="dragOver"
      @dragleave="dragLeave"
    >
      <ImageUploadAvatar
        v-if="isAvatar"
        :is-dragged-over-drop="isDraggedOverDrop"
        :preview-image="previewImage"
        :errors="errors"
        :file-size-error="fileSizeError"
        :max-allowed-size="maxAllowedSize"
        :class="[
          'image-upload-content',
          'drop-area',
          { 'drop-area--has-image': hasPreviewImage },
          { 'drop-area--dragged-over-drop': isDraggedOverDrop },
        ]"
        @onOpenFileExplorer="openFileExplorer"
      />

      <ImageUploadContent
        v-else
        :is-dragged-over-drop="isDraggedOverDrop"
        :preview-image="previewImage"
        :errors="errors"
        :file-size-error="fileSizeError"
        :max-allowed-size="maxAllowedSize"
        :allow-unsplash="allowUnsplash"
        :header-menu-inline="headerMenuInline"
        :show-image-select-icon="showImageSelectIcon"
        :preview-image-object-fit="previewImageObjectFit"
        :preview-image-object-position="previewImageObjectPosition"
        :style="{ aspectRatio: computedAspectRatio }"
        :class="[
          'image-upload-content',
          'drop-area',
          { 'drop-area--has-image': hasPreviewImage },
          { 'drop-area--dragged-over-drop': isDraggedOverDrop },
        ]"
        @onOpenUnsplash="openUnsplash"
        @onOpenFileExplorer="openFileExplorer"
      />
    </section>

    <input
      :ref="fileId"
      type="file"
      tabindex="-1"
      aria-label="upload image"
      @change="onSelectFileExplorerImage()"
    />

    <ImageUploadRepositionModal
      v-if="isRepositionToolDisplayed"
      :preview-image="previewImage"
      :aspect-ratio="computedAspectRatio"
      @closeRepositionTool="closeRepositionTool()"
      @repositionImage="onRepositionImage"
    />

    <ImageUploadUnsplash
      v-if="isUnsplashOpen"
      @onClose="closeUnsplash"
      @onSelectImage="selectUnsplashImage"
    />
  </div>
</template>

<script>
import ImageUploadAvatar from '@/components/generics/imageUpload/ImageUploadAvatar'
import ImageUploadContent from '@/components/generics/imageUpload/ImageUploadContent'
import ImageUploadHeader from '@/components/generics/imageUpload/ImageUploadHeader'
import ImageUploadRepositionModal from '@/components/generics/imageUpload/ImageUploadRepositionModal'
import ImageUploadUnsplash from '@/components/generics/imageUpload/ImageUploadUnsplash'
import { MAX_SIZE_APNG, MAX_SIZE_IMG } from '@/helpers/constants'
import { cropImage } from '@/helpers/imageCrop'
import { isFileSizeValid, isValidImageExtension } from '@/helpers/validation.js'
import { triggerUnsplashImageDownload } from '@/service/unsplash'
import { nanoid } from 'nanoid'

export default {
  components: {
    ImageUploadContent,
    ImageUploadAvatar,
    ImageUploadHeader,
    ImageUploadRepositionModal,
    ImageUploadUnsplash,
  },
  props: {
    name: {
      type: String,
      required: false,
      default: '',
    },
    existingImage: {
      type: String,
      required: false,
      default: '',
    },
    allowReposition: {
      type: Boolean,
      required: false,
      default: true,
    },
    allowRestoreOriginal: {
      type: Boolean,
      required: false,
      default: true,
    },
    allowUnsplash: {
      type: Boolean,
      required: false,
      default: true,
    },
    allowDelete: {
      type: Boolean,
      required: false,
      default: false,
    },
    aspectRatio: {
      type: String,
      required: false,
      default: '16:9',
    },
    isAvatar: {
      type: Boolean,
      required: false,
      default: false,
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    showImageSelectIcon: {
      type: Boolean,
      required: false,
      default: true,
    },
    previewImageObjectFit: {
      type: String,
      required: false,
      default: 'cover',
    },
    previewImageObjectPosition: {
      type: String,
      required: false,
      default: 'unset',
    },
    headerMenuInline: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['onImageUpload', 'onDeleteImage'],
  data() {
    return {
      fileId: `file_${nanoid()}`,
      file: null,
      previewImage: this.existingImage ?? '',
      errors: null,
      fileSizeError: 0,
      maxAllowedSize: '',
      isDraggedOverDrop: false,
      isRepositionToolDisplayed: false,
      isUnsplashOpen: false,
    }
  },
  computed: {
    hasFile() {
      return this.file !== null
    },
    hasPreviewImage() {
      return this.previewImage.length > 0
    },
    canRepositionImage() {
      return (
        this.allowReposition && this.hasPreviewImage && this.errors === null
      )
    },
    canRestoreOriginalImage() {
      return (
        this.allowRestoreOriginal &&
        this.hasFile &&
        this.existingImage.length > 0
      )
    },
    canDeleteImage() {
      return this.allowDelete && this.hasPreviewImage
    },

    uploadName() {
      return this.name || 'image_file'
    },

    computedAspectRatio() {
      switch (this.aspectRatio) {
        case '1:1':
          return 1 / 1
        case '4:1':
          return 4 / 1
        default:
          return 16 / 9
      }
    },
  },
  watch: {
    existingImage(newValue) {
      if (!this.hasFile) {
        this.previewImage = newValue || ''
      }
    },
  },
  methods: {
    openFileExplorer() {
      this.$refs[this.fileId].click() // Opens file explorer to select image (see input element in template)
    },

    onSelectFileExplorerImage() {
      const file = this.$refs[this.fileId].files[0]
      this.submitImageFile(file)
    },

    async selectUnsplashImage(image) {
      const response = await fetch(image.urls.regular)
      const blob = await response.blob()
      const file = new File([blob], 'unsplashImage.jpg', { type: blob.type })

      triggerUnsplashImageDownload(image.id)
      this.submitImageFile(file)
    },

    onDropImage(event) {
      this.isDraggedOverDrop = false
      event.stopPropagation()
      event.preventDefault()
      const file = event.dataTransfer.files[0]

      this.submitImageFile(file)
    },

    async onRepositionImage(imageData) {
      const canvas = imageData.canvas

      if (canvas) {
        if (canvas.width > 0 && canvas.height > 0) {
          const cropped = await cropImage(canvas)
          this.submitImageFile(cropped.file)
          this.closeRepositionTool()
        } else {
          alert(this.$t('image.ERROR_REPOSITION_DIMENSIONS'))
        }
      } else {
        alert(this.$t('image.ERROR_REPOSITION_UNDEFINED_CANVAS'))
      }
    },

    submitImageFile(file) {
      this.resetErrors()

      if (this.isImageValid(file)) {
        this.file = file
        this.setPreviewImageFromFile(file)

        this.$emit('onImageUpload', {
          name: this.uploadName,
          values: this.file,
        })
        this.resetFileInputValue()
      }
    },

    getMaxImageSize(imageType) {
      if (imageType === 'image/apng') return MAX_SIZE_APNG
      else return MAX_SIZE_IMG
    },

    isImageValid(image) {
      const maxAllowedSize = this.getMaxImageSize(image.type)
      const isValidSize = isFileSizeValid(image.size, maxAllowedSize)
      const isValidExtension = isValidImageExtension(image.type)

      if (!isValidExtension) {
        this.errors = { extension: true }
        return false
      }
      if (!isValidSize) {
        this.fileSizeError = image.size
        this.maxAllowedSize = `${maxAllowedSize / 1000} MB`
        this.errors = { size: true }
        return false
      }
      return true
    },

    setPreviewImageFromFile(file) {
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.previewImage = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },

    resetErrors() {
      this.errors = null
    },

    resetFileInputValue() {
      this.$refs[this.fileId].value = ''
    },

    resetForm() {
      this.resetFileInputValue()
      this.resetErrors()
      this.file = null
      this.previewImage = this.existingImage || ''
      this.$emit('onImageUpload', {
        name: this.uploadName,
        values: null,
      })
    },

    openRepositionTool() {
      this.isRepositionToolDisplayed = true
    },

    closeRepositionTool() {
      this.isRepositionToolDisplayed = false
    },

    openUnsplash() {
      this.isUnsplashOpen = true
    },

    onDeleteImage() {
      this.resetFileInputValue()
      this.resetErrors()
      this.file = null
      this.previewImage = ''

      // Without a saved image there is nothing to remove server-side, only the staged file to discard.
      if (this.existingImage.length > 0) {
        this.$emit('onDeleteImage', { name: this.uploadName })
      } else {
        this.$emit('onImageUpload', { name: this.uploadName, values: null })
      }
    },

    closeUnsplash() {
      this.isUnsplashOpen = false
    },

    dragOver(event) {
      event.preventDefault()
      this.isDraggedOverDrop = true
    },

    dragLeave(event) {
      event.preventDefault()
      this.isDraggedOverDrop = false
    },
  },
}
</script>

<style lang="scss" scoped>
.image-upload {
  max-width: 800px;
  position: relative;
}

.image-upload-header {
  margin-bottom: $spacing-4xs;

  &--inline {
    position: absolute;
    display: flex;
    justify-content: flex-end;
    padding: $spacing-3xs;
    width: 100%;
    margin-bottom: 0;
  }
}

.drop-area {
  border-radius: $border-radius-xl;
  border: 1px dashed $color-ash;
  background: $color-canvas;
  overflow: hidden;

  &--dragged-over-drop {
    border-color: $color-drag-drop;
  }
  &--has-image {
    border: 1px solid $color-border;
  }

  .image-upload-content {
    border-radius: inherit;
  }
}

input[type='file'] {
  position: absolute;
  overflow: hidden;
  opacity: 0;
  height: 0.1px;
  width: 0.1px;
  z-index: -1;
}
</style>
